import React, {useEffect, useMemo, useState} from "react";
import Dropdown from "react-bootstrap/Dropdown";
import {parseText} from "../utils/utils";
import Accordion from 'react-bootstrap/Accordion';
import Cookies from 'js-cookie';
import Form from "react-bootstrap/Form";
import {Button} from "react-bootstrap";
import AssetLabel from "./common/AssetLabel";
import CodexEvaluationCard from "./CodexEvaluationCard";
import RangeExtremumBadge, { rangeExtremumDescription } from "./common/RangeExtremumBadge";

const timeAgo = (timestamp) => {
  if (!timestamp || isNaN(timestamp) || typeof timestamp !== "number") {
    return "Invalid timestamp";
  }

  const now = new Date();
  const eventDate = new Date(timestamp * 1000);
  const diff = now - eventDate;

  if (diff < 0) {
    return "In the future";
  }

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return `${seconds} sec ago`;
  } else if (minutes < 60) {
    return `${minutes} min ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
};


function EventInfo({ eventInfo, insights, settings, setSettings }) {

  const [watchListButton, setWatchListButton] = useState({});
  const [comment, setComment] = useState("");
  const [type, setType] = useState(null);
  useEffect(() => {
    if (eventInfo && eventInfo.cur) {
      updateWatchListButton();
    }
  }, [eventInfo]);

  const dropdownItem = useMemo(() => ({
    fontSize: "10px",
    width: "100%",
    height: "100%",
    whiteSpace: "normal",
  }), []);

  const setCookie = (key, value) => {
    Cookies.set(key, value);
  }

  const recentEvents = useMemo(() => {

    if (!eventInfo || !eventInfo.cur) {
      return;
    }

    return insights.currencyInfo[eventInfo.cur]?.recentEvents?.map((eventInfo, index) => (
      <Dropdown.Item key={index} style={dropdownItem}>
        <p>
          <strong>{timeAgo(eventInfo.ts)}:</strong> {eventInfo.details}
        </p>
      </Dropdown.Item>
    ));
  }, [eventInfo?.cur, insights.currencyInfo, dropdownItem]);

  const eventsInfo = () => {

    const ei = [];

    if (insights.currencyInfo[eventInfo.cur]) {
      ei.push({label: "Price", value: insights.currencyInfo[eventInfo.cur]?.price + "$"});
    }

    ei.push({label: "Timeframe", value: eventInfo.tf.slice(1)});
    const rangeDescription = rangeExtremumDescription(eventInfo.rangeExtremum);
    if (rangeDescription) ei.push({label: "Source extremum", value: rangeDescription});

    if (insights.currencyInfo[eventInfo.cur]) {
      ei.push({label: "Rank", value: insights.currencyInfo[eventInfo.cur]?.rank});
      ei.push({label: "Funding", value: (100 * insights.currencyInfo[eventInfo.cur]?.fund)?.toFixed(4) + "%"});
      ei.push({label: "24H Vol", value: insights.currencyInfo[eventInfo.cur]?._24HVol});
      ei.push({label: "24H Change", value: percentageValue(insights.currencyInfo[eventInfo.cur]?._24H)});
      ei.push({label: "7D Change", value: percentageValue(insights.currencyInfo[eventInfo.cur]?._7D)});
      ei.push({label: "30D Change", value: percentageValue(insights.currencyInfo[eventInfo.cur]?._30D)});
      ei.push({label: "Market Cap", value: insights.currencyInfo[eventInfo.cur]?.mcap});
      if (eventInfo.sr && eventInfo.sr.length > 0) {
        ei.push({label: "Confirmations", value: eventInfo.sr || []})
      }
    }

    return ei;
  }

  const percentageValue = (val) => {
    return parseFloat(val).toFixed(2) + "%"
  }

  const actionBtnClass = (actionName) => {
      return "btn btn-" + (
          actionName === "Open Long"
          ? "success"
              : actionName === "Open Short"
                  ? "danger"
                  : "primary");
  }

  const getWatchlist = () => {

    let watchList = {};

    if (settings && settings.favourites) {
      watchList = settings.favourites;
    }

    return watchList;
  }

  const saveToWatchlist = (watchListBtn) => {
    const nextSettings = { ...(settings || {}) };
    const nextWatchList = { ...(nextSettings.favourites || {}) };
    nextWatchList[eventInfo.cur] = {
      message: watchListBtn.comment,
      type: watchListBtn.type,
      currency: eventInfo.cur,
      assetType: eventInfo.assetType
    };
    nextSettings.favourites = nextWatchList;
    setSettings(nextSettings);
    updateWatchListButton(true);
  }

  const removeFromWatchlist = () => {
    const nextSettings = { ...(settings || {}) };
    const nextWatchList = { ...(nextSettings.favourites || {}) };
    delete nextWatchList[eventInfo.cur];
    nextSettings.favourites = nextWatchList;
    setSettings(nextSettings);
    updateWatchListButton();
  }

  const updateWatchListButton = (isSaved) => {

    const watchListBtn = {...watchListButton};
    const currentWatchlistItem = getCurrentWatchListItem();

    if (!currentWatchlistItem) {

      watchListBtn.accordionTitle = "Add To Watchlist";
      watchListBtn.buttons = [{
        title: isSaved ? "Saved" : "Save",
        action: () => {
          saveToWatchlist(watchListBtn);
        },
        className: "btn btn-success"
      }];

      watchListBtn.comment = "";
      watchListBtn.type = null;
    } else {
      watchListBtn.accordionTitle = "In your watchlist";
      watchListBtn.buttons = [
        {
          title: isSaved ? "Saved" : "Save",
          action: () => {
            saveToWatchlist(watchListBtn);
          },
          className: "btn btn-success"
        },
        {
          title: "Remove",
          action: () => {
            removeFromWatchlist();
          },
          className: "btn btn-danger ms-2"
        }
      ];

      watchListBtn.comment = currentWatchlistItem.message;
      watchListBtn.type = currentWatchlistItem.type;
    }

    setWatchListButton(watchListBtn);

    if (isSaved) {
      setTimeout(() => {
          updateWatchListButton();
      }, 500)
    }
  }

  const getCurrentWatchListItem = () => {
    let watchList = settings?.favourites;
    return watchList ? watchList[eventInfo?.cur] : null;
  }

  const getPlaceholder = () => {
    return "You are going to add the " + eventInfo.cur + " to your favourites. "
        + "Please put some comments, which will be your personal reminder of the reason why it has been added.";
  }

  const watchListAction = () => {
      return (
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>{watchListButton.accordionTitle}</Accordion.Header>
              <Accordion.Body>

                <Form>
                  <Form.Group className="mb-3" controlId="comment">
                    <Form.Label>Comment</Form.Label>
                    <Form.Control
                        as="textarea"
                        placeholder={getPlaceholder()}
                        rows={3}
                        value={watchListButton.comment}
                        onChange={handleCommentChange}
                    />

                    <Form.Label className="pt-2">Type</Form.Label>
                    <Form.Select aria-label="Type"
                                 value={watchListButton.type}
                                 onChange={handleTypeChange}>
                      <option value="">---Select---</option>
                      <option value="Bullish">Bullish</option>
                      <option value="Bearish">Bearish</option>
                    </Form.Select>
                  </Form.Group>
                </Form>

                <p className="pt-2">
                  {watchListButton?.buttons?.map((btn, index) => (
                      <Button className={btn.className} onClick={btn.action}>{btn.title}</Button>
                  ))}
                </p>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
      );
  }

  const handleCommentChange = (e) => {
    watchListButton.comment = e.target.value;
    setComment(watchListButton.comment);
  };

  const handleTypeChange = (e) => {
    watchListButton.type = (e.target.value ? e.target.value : null)
    setType(watchListButton.type);
  }

  const getLinks = () => {

    const links = insights.currencyInfo[eventInfo.cur]?.links;

    const sequenceMap = new Map();
    sequenceMap.set("SPOT", { display : "Spot", link: ""});
    sequenceMap.set("FUTURES", { display : "Futures", link : ""});
    sequenceMap.set("CoinMarketCap", { display : "CMC", link : ""});
    sequenceMap.set("CoinGlass", { display : "CG", link : ""});

    if (links) {
      Object.keys(links).forEach(key => {
        if (!sequenceMap.has(key)) {
          sequenceMap.set(key, { display : key, link : ""});
        }
        sequenceMap.get(key).link = links[key];
      })
    }

    for (const key of sequenceMap.keys()) {
      if (!sequenceMap.get(key).link) {
        sequenceMap.delete(key);
      }
    }

    return sequenceMap;
  }

  return (eventInfo && (
      <section>
        <h3 className="details">
          {eventInfo ? parseText(eventInfo.name) : "Event Details"}
          {eventInfo?.cur ? (
              <AssetLabel symbol={eventInfo.cur} event={eventInfo} iconOnly className="ms-2" />
          ) : null}
        </h3>
        <RangeExtremumBadge evidence={eventInfo.rangeExtremum} />
        {eventInfo?.desc ? (
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>Details</Accordion.Header>
                <Accordion.Body>
                  {
                    eventInfo?.desc?.split("\\n").map(function (item, idx) {
                      return (
                          <p>{item}</p>
                      )
                    })
                  }
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ) : (null)}
            {eventsInfo().map(
                (info, index) => (
                    <div className="details border-bottom pt-1"><strong>{info.label}: </strong>
                      {!Array.isArray(info.value) ? info.value : (<ul style={{ margin: '5px 0', paddingLeft: '15px' }}>
                        {info.value.map((item, index) => (
                            <li key={index} style={{ margin: '2px 0' }}>{item}</li>
                        ))}</ul>)}
                    </div>
                )
            )}
          {insights.currencyInfo[eventInfo.cur]?.companies && (
              <p className="details">
                <strong>Companies: </strong>
                {insights.currencyInfo[eventInfo.cur].companies}
              </p>
          )}
          <CodexEvaluationCard latest={eventInfo.codexEvaluation} history={eventInfo.codexEvaluations || []} />
          <p className="details">
            {insights.currencyInfo[eventInfo.cur]?.bbAndMaInfo?.map(
                (info, index) => (
                    <span key={index}>
                {info}
                      <br/>
              </span>
                )
            )}
          </p>
          <h3 className="details">Bot Actions</h3>
          <div className="wrapper">
            {eventInfo?.botActions?.map((action, index) => (
                <p key={index}>
                  <a className={actionBtnClass(action.name)} href={action.url} rel="noopener noreferrer"
                     target="_blank">
                    <strong>{action.name}</strong>
                  </a>
                </p>
            ))}
          </div>
          <h3 className="details">Links</h3>
          <div className="wrapper">
            {
              Array.from(getLinks().values()).map((linkData, index) => (
                  <p>
                    <a href={linkData.link}
                       rel="noopener noreferrer"
                       target="_blank">
                      <strong>{linkData.display}</strong>
                    </a>
                  </p>
              ))
            }
          </div>
        {/*<div id="eventInfo">*/}
        {/*  <Dropdown>*/}
        {/*    <Dropdown.Toggle id="dropdown-basic-button">*/}
        {/*      Recent Events*/}
        {/*    </Dropdown.Toggle>*/}
        {/*    <Dropdown.Menu>*/}
        {/*    {recentEvents}*/}
        {/*    </Dropdown.Menu>*/}
        {/*  </Dropdown>*/}
        {/*</div>*/}

        <p key="watchlist" className="pt-2">
          {watchListAction()}
        </p>

      </section>
  ));
}

export default EventInfo;
